/**
 * echoSimpson.js — ФВ ЛЖ по Симпсону на эхо-кино: масштаб со шкалы глубины и
 * метод дисков. Чистая математика. [12.0.42]
 *
 * ПОВОД. Владелец открыл в 12.0.41 видео с Mindray DC-40 (AVI, 4-камерная
 * позиция) и спросил, можно ли по нему посчитать КДО, КСО и ФВ. Можно, но в
 * программе не было трёх вещей: масштаба (в AVI его нет), отметки кадров конца
 * диастолы и систолы и самого счёта методом дисков. Здесь — первое и третье;
 * второе делает врач, программа только запоминает номер кадра.
 *
 * 1. ШКАЛА ГЛУБИНЫ. Аппарат рисует справа от сектора линейку: короткий штрих
 *    через каждый сантиметр, длинный — через пять. На присланном кадре 800×600
 *    длинные штрихи на строках 91, 227, 363, 500, короткие — между ними через
 *    27–28 строк, и один (9 см) перекрыт значком фокуса. Отсюда 27,27 пикселя
 *    на сантиметр, 0,367 мм на пиксель.
 *
 *    Поиск нарочно строгий, потому что ошибка здесь — не отказ, а неверные
 *    миллилитры, которые выглядят правдой. Шкала признаётся только если:
 *    не меньше 8 тонких штрихов, выровненных по одному краю; все лежат на одной
 *    прямой с равным шагом (отклонение не больше 1,5 пикселя); длинные штрихи
 *    стоят ровно через пять коротких, и на их местах нет коротких. Иначе —
 *    «шкала не найдена», и масштаб вводит врач.
 *
 *    ДОПУЩЕНИЕ: пиксель квадратный — по ширине тот же масштаб, что по глубине.
 *    Шкала глубины меряет только вертикаль. Так у всех аппаратов, что я знаю,
 *    но сверить один раз с DICOM-выгрузкой того же аппарата стоит.
 *
 * 2. МЕТОД ДИСКОВ (Симпсона) в одной плоскости. Контур задаётся врачом:
 *    первая точка — фиброзное кольцо МК с одной стороны, дальше по эндокарду
 *    через верхушку, последняя — кольцо МК с другой стороны. Основание
 *    замыкается прямой. Верхушка — точка контура, дальняя от середины
 *    основания. Длинная ось делится на 20 дисков; поперечник каждого — длина
 *    сечения контура перпендикуляром к оси (если сечение пересекает контур
 *    больше двух раз — сумма внутренних отрезков).
 *
 *      V = π/4 · Σ dᵢ² · L/20
 *
 *    Проверено на полуэллипсе: объём полуэллипсоида 2/3·π·b²·a известен точно,
 *    двадцать дисков дают его с ошибкой около 0,03% (tests/echosimpson_test.mjs).
 *
 * ЧЕГО ЗДЕСЬ НЕТ, и об этом говорит окно: двухплоскостного метода (нужна
 * 2-камерная позиция), поправки на укорочение верхушки, автоматического
 * контура. Число, посчитанное по одной плоскости, предполагает круглое
 * сечение желудочка.
 *
 * Файл без импортов — правило проверяется тестом, а не запуском.
 */

// ══════════════════════════════════════════════════════════════
//  1. ШКАЛА ГЛУБИНЫ
// ══════════════════════════════════════════════════════════════

/**
 * Тонкие горизонтальные штрихи: светлые отрезки длиной 3…40 пикселей, над и
 * под которыми (через строку) темно. Соседние строки одного штриха (толщина
 * 2 пикселя) сливаются позже.
 */
function тонкиеШтрихи(серый, w, h, порог) {
  const штрихи = [];
  const светлый = (x, y) => серый[y * w + x] >= порог;
  for (let y = 2; y < h - 2; y++) {
    let x = 0;
    while (x < w) {
      if (!светлый(x, y)) { x++; continue; }
      const x0 = x;
      while (x < w && светлый(x, y)) x++;
      const L = x - x0;
      if (L < 3 || L > 40) continue;
      let над = 0, под = 0;
      for (let i = x0; i < x; i++) { if (светлый(i, y - 2)) над++; if (светлый(i, y + 2)) под++; }
      if (над / L < 0.3 && под / L < 0.3) штрихи.push({ y, x0, x1: x - 1, L });
    }
  }
  return штрихи;
}

const медиана = (м) => { const с = [...м].sort((a, b) => a - b); const n = с.length; return n ? (n % 2 ? с[(n - 1) / 2] : (с[n / 2 - 1] + с[n / 2]) / 2) : NaN; };

/** Прямая y = a + b·k методом наименьших квадратов. */
function прямая(k, y) {
  const n = k.length;
  const sk = k.reduce((s, v) => s + v, 0), sy = y.reduce((s, v) => s + v, 0);
  const skk = k.reduce((s, v) => s + v * v, 0), sky = k.reduce((s, v, i) => s + v * y[i], 0);
  const д = n * skk - sk * sk;
  if (!д) return null;
  const b = (n * sky - sk * sy) / д;
  return { a: (sy - b * sk) / n, b };
}

/** Проверить одну группу штрихов, выровненных по краю. */
function проверитьГруппу(группа) {
  // Слить строки одного штриха (толщина больше пикселя).
  const по_y = [...группа].sort((p, q) => p.y - q.y);
  const штрихи = [];
  for (const ш of по_y) {
    const посл = штрихи[штрихи.length - 1];
    if (посл && ш.y - посл.yМакс <= 2) { посл.yМакс = ш.y; посл.сумма += ш.y; посл.n++; посл.L = Math.max(посл.L, ш.L); }
    else штрихи.push({ yМин: ш.y, yМакс: ш.y, сумма: ш.y, n: 1, L: ш.L });
  }
  if (штрихи.length < 8) return { ок: false, причина: `штрихов ${штрихи.length}, нужно не меньше 8` };
  const y = штрихи.map((ш) => ш.сумма / ш.n);
  const разности = y.slice(1).map((v, i) => v - y[i]);
  // Шаг — через весь размах, а не медианой соседних разностей: при неровных
  // штрихах медиана попадает то на короткую, то на длинную разность, и номера
  // штрихов сталкиваются. Медиана нужна только чтобы сосчитать интервалы
  // (пропущенный штрих — два интервала).
  const грубо = медиана(разности);
  if (!(грубо >= 5)) return { ок: false, причина: 'шаг штрихов меньше 5 пикселей' };
  const интервалов = разности.reduce((s, d) => s + Math.max(1, Math.round(d / грубо)), 0);
  const шаг0 = (y[y.length - 1] - y[0]) / интервалов;

  // Номер каждого штриха от первого по оценке шага; пропуски допустимы.
  let k = y.map((v) => Math.round((v - y[0]) / шаг0));
  let ф = прямая(k, y);
  if (!ф) return { ок: false, причина: 'штрихи не ложатся на прямую' };
  // Уточнить номера по найденной прямой (на длинной шкале шаг0 мог набежать).
  k = y.map((v) => Math.round((v - ф.a) / ф.b));
  ф = прямая(k, y);
  if (!ф) return { ок: false, причина: 'штрихи не ложатся на прямую' };
  if (new Set(k).size !== k.length) return { ок: false, причина: 'два штриха на одном месте шкалы' };
  const невязки = y.map((v, i) => Math.abs(v - (ф.a + ф.b * k[i])));
  const невязкаМакс = Math.max(...невязки);
  if (невязкаМакс > 1.5) return { ок: false, причина: `штрихи не равномерны: отклонение ${невязкаМакс.toFixed(1)} пикс` };

  // Длинные — через пять коротких, и только там.
  const короткая = медиана(штрихи.map((ш) => ш.L));
  const длинные = штрихи.map((ш, i) => ({ k: k[i], длинный: ш.L >= 1.5 * короткая }));
  const kДлинных = длинные.filter((д) => д.длинный).map((д) => д.k);
  if (kДлинных.length < 2) return { ок: false, причина: 'длинных штрихов меньше двух — не понять, сколько сантиметров в делении' };
  const остаток = ((kДлинных[0] % 5) + 5) % 5;
  if (!kДлинных.every((v) => ((v % 5) + 5) % 5 === остаток)) return { ок: false, причина: 'длинные штрихи стоят не через пять коротких' };
  if (длинные.some((д) => !д.длинный && ((д.k % 5) + 5) % 5 === остаток)) return { ок: false, причина: 'на месте длинного штриха стоит короткий' };

  const пикселейНаСм = ф.b;
  if (!(пикселейНаСм >= 5 && пикселейНаСм <= 200)) return { ок: false, причина: `${пикселейНаСм.toFixed(1)} пикс на см — вне разумного` };
  const смМеждуДлинными = Math.max(...kДлинных) - Math.min(...kДлинных);
  return {
    ок: true,
    ммНаПиксель: 10 / пикселейНаСм,
    пикселейНаСм,
    штрихов: штрихи.length,
    длинных: kДлинных.length,
    смМеждуДлинными,
    пикселейМеждуДлинными: смМеждуДлинными * пикселейНаСм,
    невязкаМакс,
  };
}

/**
 * Найти шкалу глубины на кадре.
 *
 * @param {Uint8Array|Uint8ClampedArray|number[]} серый яркость 0…255, по строкам
 * @param {number} ширина
 * @param {number} высота
 * @param {{порог?: number}} [опции]
 * @returns {{ок: true, ммНаПиксель: number, пикселейНаСм: number, штрихов: number, длинных: number,
 *            смМеждуДлинными: number, пикселейМеждуДлинными: number, невязкаМакс: number,
 *            край: number, выравнивание: 'справа'|'слева'} | {ок: false, причина: string}}
 */
export function шкалаГлубины(серый, ширина, высота, { порог = 90 } = {}) {
  if (!серый || !(ширина > 10) || !(высота > 10) || серый.length < ширина * высота) {
    return { ок: false, причина: 'нет кадра' };
  }
  const штрихи = тонкиеШтрихи(серый, ширина, высота, порог);
  let лучшая = null;
  // Причина отказа — от САМОЙ БОЛЬШОЙ отвергнутой группы: та же шкала,
  // собранная по другому краю, иначе перебивала бы объяснение своим.
  let причина = 'на кадре нет ряда тонких штрихов';
  let размерПричины = 0;
  for (const [поле, выравнивание] of [['x1', 'справа'], ['x0', 'слева']]) {
    const по_краю = new Map();
    for (const ш of штрихи) {
      const к = ш[поле];
      if (!по_краю.has(к)) по_краю.set(к, []);
      по_краю.get(к).push(ш);
    }
    for (const край of по_краю.keys()) {
      // Край ±1 пиксель: сглаживание JPEG сдвигает конец штриха.
      const группа = [...(по_краю.get(край - 1) || []), ...по_краю.get(край), ...(по_краю.get(край + 1) || [])];
      if (группа.length < 8) continue;
      const р = проверитьГруппу(группа);
      if (!р.ок) { if (группа.length > размерПричины) { причина = р.причина; размерПричины = группа.length; } continue; }
      if (!лучшая || р.штрихов > лучшая.штрихов) лучшая = { ...р, край, выравнивание };
    }
  }
  return лучшая || { ок: false, причина: 'шкала глубины не найдена: ' + причина };
}

/**
 * Pixel Spacing из текста тега «строки\столбцы» → [a, b] или null.
 *
 * Шаг РОВНО 1\1 не принимается: это подстановка программы, когда тега нет
 * (loaderWorkerBridge: «ps по умолчанию [1, 1]»), и запасной путь чтения тегов
 * может вернуть именно её. Для УЗИ 1 мм на пиксель неправдоподобен.
 */
export function шагИзТега(значение) {
  const ч = String(значение ?? '').split('\\').map((x) => Number(String(x).trim()));
  if (ч.length !== 2 || !ч.every((v) => Number.isFinite(v) && v > 0 && v < 10)) return null;
  if (ч.every((v) => Math.abs(v - 1) < 1e-6)) return null;
  return ч;
}

/**
 * Какой масштаб брать: Pixel Spacing файла → шкала глубины на кадре → нет.
 * Файлу верим больше, но расхождение со шкалой больше 2% называем вслух.
 *
 * @param {number[]|null} файл результат шагИзТега
 * @param {ReturnType<typeof шкалаГлубины>|null} шкала
 * @returns {{мм: number|null, откуда: string, расхождение: number|null}}
 */
export function выбратьМасштаб(файл, шкала) {
  const ш = шкала?.ок ? шкала : null;
  const з = (x, n) => x.toFixed(n).replace('.', ',');
  if (Array.isArray(файл) && файл.length === 2) {
    const мм = (файл[0] + файл[1]) / 2;
    if (!ш) return { мм, откуда: 'Pixel Spacing из файла', расхождение: null };
    const расхождение = Math.abs(ш.ммНаПиксель - мм) / мм;
    return {
      мм,
      откуда: расхождение < 0.02 ? 'Pixel Spacing из файла, совпадает со шкалой глубины на кадре'
        : `Pixel Spacing из файла; шкала глубины на кадре даёт ${з(ш.ммНаПиксель, 4)} — расхождение ${з(расхождение * 100, 1)}%, проверьте`,
      расхождение,
    };
  }
  if (ш) {
    return { мм: ш.ммНаПиксель, откуда: `шкала глубины на кадре: ${ш.смМеждуДлинными} см = ${Math.round(ш.пикселейМеждуДлинными)} пикс`, расхождение: null };
  }
  return { мм: null, откуда: 'масштаба нет — введите мм на пиксель; без него посчитается только ФВ', расхождение: null };
}

// ══════════════════════════════════════════════════════════════
//  2. МЕТОД ДИСКОВ
// ══════════════════════════════════════════════════════════════

const расст = (p, q) => Math.hypot(p[0] - q[0], p[1] - q[1]);

/**
 * Объём полости ЛЖ методом дисков по одному контуру.
 *
 * @param {Array<[number, number]>} точки контур в пикселях: кольцо МК → по
 *   эндокарду через верхушку → кольцо МК с другой стороны
 * @param {number|[number, number]} ммНаПиксель один масштаб или [по x, по y]
 * @param {{дисков?: number, кольцо?: string}} [опции] кольцо — [12.0.43] имя клапана-основания в текстах отказа
 *   (МК для ЛЖ и ЛП, ТК для ПП и ПЖ)
 * @returns {{ок: true, объёмМЛ: number, длинаММ: number, основаниеММ: number, площадьММ2: number, поперечникМаксММ: number,
 *            объёмПлощадьДлинаМЛ: number, дисков: number, хорды: Array<[[number,number],[number,number]]>,
 *            верхушка: [number, number], серединаОснования: [number, number]} | {ок: false, причина: string}}
 *   хорды, верхушка и середина основания — в пикселях, для рисования поверх снимка
 */
export function объёмДисками(точки, ммНаПиксель, { дисков = 20, кольцо = 'МК' } = {}) {
  if (!Array.isArray(точки) || точки.length < 5) {
    return { ок: false, причина: `нужно не меньше 5 точек: кольцо ${кольцо}, три по стенке, кольцо ${кольцо}` };
  }
  const s = Array.isArray(ммНаПиксель) ? ммНаПиксель : [ммНаПиксель, ммНаПиксель];
  if (!(s[0] > 0 && s[1] > 0)) return { ок: false, причина: 'нет масштаба (мм на пиксель)' };
  if (!точки.every((p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]))) {
    return { ок: false, причина: 'в контуре есть точка без координат' };
  }
  const P = точки.map((p) => [p[0] * s[0], p[1] * s[1]]);           // в мм
  const К0 = P[0], К1 = P[P.length - 1];
  const основаниеММ = расст(К0, К1);
  if (основаниеММ < 1) return { ок: false, причина: `точки кольца ${кольцо} совпадают — первая и последняя точки должны стоять по разные стороны клапана` };
  const M = [(К0[0] + К1[0]) / 2, (К0[1] + К1[1]) / 2];

  let iВерх = -1, L = 0;
  for (let i = 0; i < P.length; i++) { const d = расст(P[i], M); if (d > L) { L = d; iВерх = i; } }
  if (iВерх === 0 || iВерх === P.length - 1) {
    return { ок: false, причина: `верхушка не найдена: дальше всего от середины основания оказалось само кольцо ${кольцо} — проверьте контур` };
  }
  if (L < 5) return { ок: false, причина: 'длина полости меньше 5 мм — проверьте масштаб и контур' };

  const u = [(P[iВерх][0] - M[0]) / L, (P[iВерх][1] - M[1]) / L];
  const v = [-u[1], u[0]];
  const h = L / дисков;
  const n = P.length;
  let V = 0, поперечникМаксММ = 0;
  const хорды = [];
  const поперечникиММ = [];   // [12.0.47] по дискам, от основания к верхушке — для двух плоскостей
  for (let i = 0; i < дисков; i++) {
    const t = (i + 0.5) * h;
    const C = [M[0] + t * u[0], M[1] + t * u[1]];
    const пересечения = [];
    for (let j = 0; j < n; j++) {
      const A = P[j], B = P[(j + 1) % n];                           // последнее ребро — основание
      const da = (A[0] - C[0]) * u[0] + (A[1] - C[1]) * u[1];
      const db = (B[0] - C[0]) * u[0] + (B[1] - C[1]) * u[1];
      if ((da <= 0 && db > 0) || (db <= 0 && da > 0)) {
        const r = da / (da - db);
        const X = [A[0] + r * (B[0] - A[0]), A[1] + r * (B[1] - A[1])];
        пересечения.push((X[0] - C[0]) * v[0] + (X[1] - C[1]) * v[1]);
      }
    }
    пересечения.sort((a, b) => a - b);
    let d = 0;
    if (пересечения.length >= 2 && пересечения.length % 2 === 0) {
      for (let k = 0; k < пересечения.length; k += 2) d += пересечения[k + 1] - пересечения[k];
    } else if (пересечения.length >= 2) {
      d = пересечения[пересечения.length - 1] - пересечения[0];
    }
    V += (Math.PI / 4) * d * d * h;
    поперечникиММ.push(d);
    if (d > поперечникМаксММ) поперечникМаксММ = d;
    if (пересечения.length >= 2) {
      const a = пересечения[0], b = пересечения[пересечения.length - 1];
      хорды.push([
        [(C[0] + a * v[0]) / s[0], (C[1] + a * v[1]) / s[1]],
        [(C[0] + b * v[0]) / s[0], (C[1] + b * v[1]) / s[1]],
      ]);
    }
  }

  let площадь2 = 0;
  for (let j = 0; j < n; j++) { const A = P[j], B = P[(j + 1) % n]; площадь2 += A[0] * B[1] - B[0] * A[1]; }
  const площадьММ2 = Math.abs(площадь2) / 2;

  return {
    ок: true,
    объёмМЛ: V / 1000,
    длинаММ: L,
    основаниеММ,
    площадьММ2,
    поперечникМаксММ,
    поперечникиММ,
    объёмПлощадьДлинаМЛ: (8 * площадьММ2 * площадьММ2) / (3 * Math.PI * L) / 1000,
    дисков,
    хорды,
    верхушка: [P[iВерх][0] / s[0], P[iВерх][1] / s[1]],
    серединаОснования: [M[0] / s[0], M[1] / s[1]],
  };
}

// ══════════════════════════════════════════════════════════════
//  3. ИТОГ: КДО, КСО, УО, ФВ, ЧСС, МО
// ══════════════════════════════════════════════════════════════

/**
 * @param {{кд: ReturnType<typeof объёмДисками>, кс: ReturnType<typeof объёмДисками>,
 *          кадровВЦикле?: number|null, мсНаКадр?: number|null}} p
 */
export function итогСимпсона({ кд, кс, кадровВЦикле = null, мсНаКадр = null } = {}) {
  if (!кд?.ок) return { ок: false, причина: 'нет контура конца диастолы' };
  if (!кс?.ок) return { ок: false, причина: 'нет контура конца систолы' };
  const кдо = кд.объёмМЛ, ксо = кс.объёмМЛ;
  if (!(кдо > 0)) return { ок: false, причина: 'объём в конце диастолы нулевой' };
  if (ксо >= кдо) {
    return { ок: false, причина: `КСО (${ксо.toFixed(0)} мл) не меньше КДО (${кдо.toFixed(0)} мл) — не перепутаны ли кадры диастолы и систолы?` };
  }
  const уо = кдо - ксо;
  const итог = { ок: true, кдоМЛ: кдо, ксоМЛ: ксо, уоМЛ: уо, фв: уо / кдо, чсс: null, моЛМин: null, циклМС: null, оговорки: [] };
  if (кадровВЦикле > 0 && мсНаКадр > 0) {
    итог.циклМС = кадровВЦикле * мсНаКадр;
    итог.чсс = 60000 / итог.циклМС;
    итог.моЛМин = (уо * итог.чсс) / 1000;
    if (итог.чсс < 30 || итог.чсс > 220) итог.оговорки.push(`ЧСС ${итог.чсс.toFixed(0)} в минуту — вне разумного: проверьте кадры двух соседних КД`);
  }
  if (итог.фв < 0.05 || итог.фв > 0.9) итог.оговорки.push(`ФВ ${(итог.фв * 100).toFixed(0)}% — вне правдоподобного: проверьте контуры и кадры`);
  const отношение = кс.длинаММ / кд.длинаММ;
  if (отношение > 1.05) итог.оговорки.push('в систолу желудочек длиннее, чем в диастолу — вероятно, в одном из кадров укорочена верхушка');
  return итог;
}

/** Оговорки, которые окно показывает всегда. */
export function оговоркиСимпсона() {
  return [
    'Метод дисков в одной плоскости (4-камерная позиция): предполагается круглое сечение желудочка. Двухплоскостной метод (плюс 2-камерная) точнее.',
    'Укорочение верхушки в кадре занижает объёмы.',
    'Кадры конца диастолы и систолы выбирает врач; программа их не угадывает.',
  ];
}


// ══════════════════════════════════════════════════════════════
//  3а. ДВЕ ПЛОСКОСТИ (4- и 2-камерная)  [12.0.47]
// ══════════════════════════════════════════════════════════════
//
// Метод дисков в двух плоскостях: диск — эллипс с поперечниками aᵢ (4-камерная)
// и bᵢ (2-камерная) на одной высоте:
//   V = π/4 · Σ aᵢ·bᵢ · L/20,  L — БОЛЬШАЯ из двух длин.
// Каждый контур делится на 20 дисков по своей длине, i-й диск — на одной доле
// длины в обеих позициях. Проверено на полуэллипсоиде с разными полуосями в
// двух плоскостях: 2/3·π·a·b·c (tests/echosimpson_test.mjs). Длины в двух
// позициях должны быть близки: разница больше 10% (порог по памяти) обычно
// значит, что в одной из них укорочена верхушка, — окно это пишет.

/** Объём двумя плоскостями по двум результатам объёмДисками. */
export function объёмБиплан(к4, к2) {
  if (!к4?.ок) return { ок: false, причина: 'нет контура 4-камерной позиции' };
  if (!к2?.ок) return { ок: false, причина: 'нет контура 2-камерной позиции' };
  const a = к4.поперечникиММ, b = к2.поперечникиММ;
  if (!a || !b || a.length !== b.length) return { ок: false, причина: 'контуры разделены на разное число дисков' };
  const L = Math.max(к4.длинаММ, к2.длинаММ);
  let сумма = 0;
  for (let i = 0; i < a.length; i++) сумма += a[i] * b[i];
  const разницаДлин = Math.abs(к4.длинаММ - к2.длинаММ) / L;
  return { ок: true, объёмМЛ: (Math.PI / 4) * сумма * (L / a.length) / 1000, длинаММ: L, разницаДлин };
}

/**
 * КДО, КСО, УО, ФВ двумя плоскостями. Нет 2-камерных контуров — ok:false с
 * причиной, окно тогда показывает одноплоскостной итог.
 */
export function итогБиплана({ кд4, кс4, кд2, кс2, кадровВЦикле = null, мсНаКадр = null } = {}) {
  if (!кд2?.ок || !кс2?.ок) return { ок: false, причина: 'для двух плоскостей обведите КД и КС ещё и в 2-камерной позиции' };
  const кд = объёмБиплан(кд4, кд2), кс = объёмБиплан(кс4, кс2);
  if (!кд.ок) return кд;
  if (!кс.ок) return кс;
  // Тот же итог, что для одной плоскости: подставляем объёмы двух плоскостей.
  const и = итогСимпсона({ кд: { ок: true, объёмМЛ: кд.объёмМЛ, длинаММ: кд.длинаММ }, кс: { ок: true, объёмМЛ: кс.объёмМЛ, длинаММ: кс.длинаММ }, кадровВЦикле, мсНаКадр });
  if (!и.ок) return и;
  for (const [имя, x] of [['конце диастолы', кд], ['конце систолы', кс]]) {
    if (x.разницаДлин > 0.1) и.оговорки.push(`В ${имя} длина ЛЖ в 4- и 2-камерной позициях различается на ${Math.round(x.разницаДлин * 100)}% (больше 10%) — вероятно, в одной из них укорочена верхушка.`);
  }
  return { ...и, двеПлоскости: true };
}

// ══════════════════════════════════════════════════════════════
//  3б. ПАРАСТЕРНАЛЬНАЯ ДЛИННАЯ ОСЬ  [12.0.47]
// ══════════════════════════════════════════════════════════════
//
// Линейные размеры ЛЖ (ASE/EACVI 2015): МЖП, КДР, ЗСЛЖ — в конце диастолы,
// КСР — в конце систолы; масса миокарда по «кубической» формуле ASE:
//   ММЛЖ = 0,8 · 1,04 · [(МЖП + КДР + ЗСЛЖ)³ − КДР³] + 0,6 г   (в сантиметрах),
// относительная толщина стенок ОТС = 2·ЗСЛЖ / КДР, укорочение ФУ = (КДР − КСР)/КДР.
// Геометрия ЛЖ — по индексу массы и ОТС (те же рекомендации).
//
// ПОРОГИ ПО ПАМЯТИ (ASE/EACVI 2015), раздельно для женщин и мужчин; без пола
// они не применяются — окно просит его указать. Корень аорты и ВТЛЖ без
// порогов: их норма зависит от площади тела и возраста.

export const ПОРОГИ_ДО = Object.freeze({
  стенка: { ж: 9, м: 10, ед: 'мм', источник: 'ASE/EACVI 2015' },
  кдр:    { ж: 52, м: 58, ед: 'мм', источник: 'ASE/EACVI 2015' },
  кср:    { ж: 35, м: 40, ед: 'мм', источник: 'ASE/EACVI 2015' },
  лп:     { ж: 38, м: 40, ед: 'мм', источник: 'ASE/EACVI 2015' },
  иммлж:  { ж: 95, м: 115, ед: 'г/м²', источник: 'ASE/EACVI 2015' },
  отс:    { выше: 0.42, источник: 'ASE/EACVI 2015' },
});

/** Масса миокарда ЛЖ, г, по кубической формуле ASE; размеры в мм. */
export function массаЛЖ(мжпММ, кдрММ, зслжММ) {
  const [a, b, c] = [мжпММ, кдрММ, зслжММ].map((x) => Number(x) / 10);
  if (![a, b, c].every((x) => x > 0)) return null;
  return 0.8 * 1.04 * ((a + b + c) ** 3 - b ** 3) + 0.6;
}

/**
 * Сводка по длинной оси. Входы — результаты отрезокММ (или null).
 * @returns {{строки:object[], оговорки:string[], ппт:number|null, геометрия:string|null}}
 */
export function итогДлиннойОси({ мжп, кдр, зслж, кср, втлж, аорта, лп, пол = null, рост = null, вес = null } = {}) {
  const ок = (x) => x?.ок;
  const мм = (x) => (ок(x) ? x.длинаММ : null);
  const S = ппт(рост, вес);
  const п = пол === 'м' || пол === 'ж' ? пол : null;
  const строки = [], оговорки = [];
  const р = (группа, имя, значение, ед, знаков, вне = null) => строки.push({ группа, имя, значение, ед, знаков, вне });
  const выше = (v, порог) => (п && v > порог[п] ? `выше ${String(порог[п]).replace('.', ',')} ${порог.ед} у ${п === 'ж' ? 'женщин' : 'мужчин'} (${порог.источник})` : null);
  if (ок(мжп)) р('ЛЖ, конец диастолы', 'МЖП', мм(мжп), 'мм', 0, выше(мм(мжп), ПОРОГИ_ДО.стенка));
  if (ок(кдр)) р('ЛЖ, конец диастолы', 'КДР', мм(кдр), 'мм', 0, выше(мм(кдр), ПОРОГИ_ДО.кдр));
  if (ок(зслж)) р('ЛЖ, конец диастолы', 'ЗСЛЖ', мм(зслж), 'мм', 0, выше(мм(зслж), ПОРОГИ_ДО.стенка));
  if (ок(кср)) р('ЛЖ, конец систолы', 'КСР', мм(кср), 'мм', 0, выше(мм(кср), ПОРОГИ_ДО.кср));
  if (ок(кдр) && ок(кср)) {
    if (мм(кср) >= мм(кдр)) оговорки.push('КСР не меньше КДР — не перепутаны ли кадры диастолы и систолы?');
    else р('ЛЖ, конец систолы', 'ФУ (фракция укорочения)', ((мм(кдр) - мм(кср)) / мм(кдр)) * 100, '%', 0);
  }
  let геометрия = null;
  if (ок(мжп) && ок(кдр) && ок(зслж)) {
    const масса = массаЛЖ(мм(мжп), мм(кдр), мм(зслж));
    const отс = (2 * мм(зслж)) / мм(кдр);
    р('Масса и геометрия', 'Масса миокарда ЛЖ (ASE, кубическая)', масса, 'г', 0);
    р('Масса и геометрия', 'ОТС (2·ЗСЛЖ / КДР)', отс, '', 2, отс > ПОРОГИ_ДО.отс.выше ? `выше ${String(ПОРОГИ_ДО.отс.выше).replace('.', ',')} (${ПОРОГИ_ДО.отс.источник})` : null);
    if (S) {
      const и = масса / S;
      р('Масса и геометрия', 'Индекс массы миокарда ЛЖ', и, 'г/м²', 0, выше(и, ПОРОГИ_ДО.иммлж));
      if (п) {
        const гипер = и > ПОРОГИ_ДО.иммлж[п], конц = отс > ПОРОГИ_ДО.отс.выше;
        геометрия = гипер ? (конц ? 'концентрическая гипертрофия' : 'эксцентрическая гипертрофия') : (конц ? 'концентрическое ремоделирование' : 'нормальная геометрия');
      } else {
        оговорки.push('Геометрия ЛЖ и пороги размеров — после выбора пола: нормы у женщин и мужчин разные.');
      }
    } else {
      оговорки.push('Индекс массы и геометрия ЛЖ — после ввода роста и веса.');
    }
  }
  if (ок(втлж)) {
    р('Выносящий тракт, аорта, ЛП', 'ВТЛЖ', мм(втлж), 'мм', 0);
    р('Выносящий тракт, аорта, ЛП', 'Площадь ВТЛЖ (π·d²/4)', Math.PI * (мм(втлж) / 10) ** 2 / 4, 'см²', 2);
  }
  if (ок(аорта)) р('Выносящий тракт, аорта, ЛП', 'Корень аорты (синусы)', мм(аорта), 'мм', 0);
  if (ок(лп)) р('Выносящий тракт, аорта, ЛП', 'ЛП (переднезадний)', мм(лп), 'мм', 0, выше(мм(лп), ПОРОГИ_ДО.лп));
  if (строки.length && !п) оговорки.push('Пол не указан — пороги, зависящие от пола, не применены.');
  return { строки, оговорки, ппт: S, геометрия };
}

export function оговоркиДлиннойОси() {
  return [
    'Размеры — перпендикулярно длинной оси ЛЖ, на уровне концов створок МК (чуть ниже), от края до края эндокарда (граница «ткань — кровь»).',
    'Масса по линейным размерам предполагает правильную форму ЛЖ; при асимметричной гипертрофии или аневризме она неточна.',
    'Пороги перенесены из рекомендаций ASE/EACVI 2015 по памяти — сверьте с действующими; «выше порога» — рамка для глаза, не диагноз.',
  ];
}

// ══════════════════════════════════════════════════════════════
//  4. КАМЕРЫ И КОЛЬЦА  [12.0.43]
// ══════════════════════════════════════════════════════════════
//
// Из того же 4-камерного клипа: ЛП и ПП (обводка по кольцу МК/ТК через крышу —
// те же диски, что у ЛЖ: основание — кольцо, «верхушка» — крыша), ПЖ (площадь
// в конце диастолы и систолы → FAC), три отрезка ПЖ, кольца ТК и МК, а для ЛЖ
// — площади, FAC и индекс сферичности из контуров Симпсона.
//
// ПОРОГИ ПЕРЕНЕСЕНЫ ПО ПАМЯТИ из рекомендаций, источник назван у каждого. Это
// рамки для глаза («выше порога»), а не диагноз, и окно так и пишет. Сверьте
// с действующими рекомендациями — исправить число можно здесь, в одном месте.

export const ПОРОГИ = Object.freeze({
  иоЛП:   { выше: 34, ед: 'мл/м²', источник: 'ASE/EACVI 2015' },
  площПП: { выше: 18, ед: 'см²',   источник: 'ASE 2010, правые камеры' },
  фацПЖ:  { ниже: 35, ед: '%',     источник: 'ASE/EACVI 2015' },
  пж1:    { выше: 41, ед: 'мм',    источник: 'ASE/EACVI 2015' },
  пж2:    { выше: 35, ед: 'мм',    источник: 'ASE/EACVI 2015' },
  пж3:    { выше: 83, ед: 'мм',    источник: 'ASE/EACVI 2015' },
  тк:     { неМеньше: 40, ед: 'мм', источник: 'ESC/EACTS 2021' },
  ткИндекс: { неМеньше: 21, ед: 'мм/м²', источник: 'ESC/EACTS 2021' },
});

/**
 * [12.0.45] Масштаб по двум меткам линейки, которые отметил врач.
 *
 * ПОВОД. У архивных клипов владельца (Cinepak, два аппарата) метки глубины
 * идут вдоль края сектора, наклонно, и только с одной стороны. Автоматически
 * их находить на этих клипах ненадёжно: у Philips на одном краю нашлись две
 * ровные серии меток — через 14,9 и через 29,7 пикселя, — и по картинке не
 * понять, какая из них сантиметр. Неверный масштаб выглядит правдоподобно,
 * поэтому решает врач: два щелчка по меткам и сколько сантиметров между ними
 * (между «5» и «10» — 5). Подходит для любого аппарата и любой линейки.
 *
 * @returns {{ок:boolean, причина?:string, ммНаПиксель?:number, пикс?:number}}
 */
export function масштабПоМеткам(p, q, см) {
  if (![p, q].every((т) => Array.isArray(т) && Number.isFinite(т[0]) && Number.isFinite(т[1]))) return { ок: false, причина: 'нужны две метки линейки' };
  const пикс = Math.hypot(q[0] - p[0], q[1] - p[1]);
  const c = Number(см);
  if (!(c >= 0.5 && c <= 30)) return { ок: false, причина: 'расстояние между метками — от 0,5 до 30 см' };
  if (пикс < 20) return { ок: false, причина: 'метки ближе 20 пикселей — возьмите метки дальше друг от друга (например, «5» и «10»)' };
  const ммНаПиксель = (c * 10) / пикс;
  if (!(ммНаПиксель > 0.01 && ммНаПиксель < 10)) return { ок: false, причина: 'получился невозможный масштаб — проверьте метки и сантиметры' };
  return { ок: true, ммНаПиксель, пикс };
}

/** Длина отрезка в мм по двум точкам в пикселях. */
export function отрезокММ(p, q, ммНаПиксель) {
  const s = Array.isArray(ммНаПиксель) ? ммНаПиксель : [ммНаПиксель, ммНаПиксель];
  if (!(s[0] > 0 && s[1] > 0)) return { ок: false, причина: 'нет масштаба (мм на пиксель)' };
  if (![p, q].every((т) => Array.isArray(т) && Number.isFinite(т[0]) && Number.isFinite(т[1]))) return { ок: false, причина: 'нужны две точки' };
  const д = Math.hypot((q[0] - p[0]) * s[0], (q[1] - p[1]) * s[1]);
  if (д < 1) return { ок: false, причина: 'точки совпадают' };
  return { ок: true, длинаММ: д };
}

/** Площадь поверхности тела, м², по Мостеллеру: √(рост·вес/3600). */
export function ппт(ростСм, весКг) {
  const р = Number(ростСм), в = Number(весКг);
  if (!(р >= 40 && р <= 250) || !(в >= 2 && в <= 400)) return null;
  return Math.sqrt((р * в) / 3600);
}

/**
 * Сводка по камерам и кольцам. Каждый вход — результат объёмДисками или
 * отрезокММ (или null, если не обведено). Строка появляется, только если
 * для неё есть данные; индексы — только при известной ППТ.
 *
 * @returns {{строки: Array<{группа:string, имя:string, значение:number, ед:string, знаков:number,
 *            вне?:string}>, оговорки: string[], ппт: number|null}}
 */
export function итогКамер({ лжКД, лжКС, лп, пп, пжКД, пжКС, пж1, пж2, пж3, тк, мк, рост, вес } = {}) {
  const строки = [];
  const оговорки = [];
  const S = ппт(рост, вес);
  const ок = (x) => x && x.ок;
  const р = (группа, имя, значение, ед, знаков, вне) => строки.push({ группа, имя, значение, ед, знаков, ...(вне ? { вне } : {}) });
  const выше = (v, п) => (v > п.выше ? `выше ${п.выше} ${п.ед} (${п.источник})` : undefined);
  const ниже = (v, п) => (v < п.ниже ? `ниже ${п.ниже} ${п.ед} (${п.источник})` : undefined);
  const неМеньше = (v, п) => (v >= п.неМеньше ? `${п.неМеньше} ${п.ед} и больше (${п.источник})` : undefined);

  // ЛЖ — из контуров Симпсона
  if (ок(лжКД)) {
    р('ЛЖ', 'Площадь ЛЖ, конец диастолы', лжКД.площадьММ2 / 100, 'см²', 1);
    if (лжКД.поперечникМаксММ > 0) р('ЛЖ', 'Индекс сферичности (длина / поперечник, КД)', лжКД.длинаММ / лжКД.поперечникМаксММ, '', 2);
  }
  if (ок(лжКС)) р('ЛЖ', 'Площадь ЛЖ, конец систолы', лжКС.площадьММ2 / 100, 'см²', 1);
  if (ок(лжКД) && ок(лжКС)) {
    if (лжКС.площадьММ2 < лжКД.площадьММ2) р('ЛЖ', 'FAC ЛЖ', (1 - лжКС.площадьММ2 / лжКД.площадьММ2) * 100, '%', 0);
    else оговорки.push('Площадь ЛЖ в систолу не меньше, чем в диастолу — FAC ЛЖ не считается: не перепутаны ли кадры?');
  }
  // ЛП
  if (ок(лп)) {
    р('ЛП', 'Площадь ЛП', лп.площадьММ2 / 100, 'см²', 1);
    р('ЛП', 'Объём ЛП (диски, одна плоскость)', лп.объёмМЛ, 'мл', 0);
    р('ЛП', 'Объём ЛП (площадь-длина)', лп.объёмПлощадьДлинаМЛ, 'мл', 0);
    if (S) { const и = лп.объёмМЛ / S; р('ЛП', 'Индекс объёма ЛП', и, 'мл/м²', 0, выше(и, ПОРОГИ.иоЛП)); }
  }
  // ПП
  if (ок(пп)) {
    const а = пп.площадьММ2 / 100;
    р('ПП', 'Площадь ПП', а, 'см²', 1, выше(а, ПОРОГИ.площПП));
    р('ПП', 'Объём ПП (диски, одна плоскость)', пп.объёмМЛ, 'мл', 0);
    if (S) р('ПП', 'Индекс объёма ПП', пп.объёмМЛ / S, 'мл/м²', 0);
  }
  // ПЖ
  if (ок(пжКД)) р('ПЖ', 'Площадь ПЖ, конец диастолы', пжКД.площадьММ2 / 100, 'см²', 1);
  if (ок(пжКС)) р('ПЖ', 'Площадь ПЖ, конец систолы', пжКС.площадьММ2 / 100, 'см²', 1);
  if (ок(пжКД) && ок(пжКС)) {
    if (пжКС.площадьММ2 < пжКД.площадьММ2) {
      const ф = (1 - пжКС.площадьММ2 / пжКД.площадьММ2) * 100;
      р('ПЖ', 'FAC ПЖ', ф, '%', 0, ниже(ф, ПОРОГИ.фацПЖ));
    } else оговорки.push('Площадь ПЖ в систолу не меньше, чем в диастолу — FAC ПЖ не считается: не перепутаны ли кадры?');
  }
  if (ок(пж1)) р('ПЖ', 'ПЖ базальный поперечник', пж1.длинаММ, 'мм', 0, выше(пж1.длинаММ, ПОРОГИ.пж1));
  if (ок(пж2)) р('ПЖ', 'ПЖ средний поперечник', пж2.длинаММ, 'мм', 0, выше(пж2.длинаММ, ПОРОГИ.пж2));
  if (ок(пж3)) р('ПЖ', 'ПЖ длина', пж3.длинаММ, 'мм', 0, выше(пж3.длинаММ, ПОРОГИ.пж3));
  // Кольца
  if (ок(тк)) {
    р('Кольца', 'Кольцо ТК', тк.длинаММ, 'мм', 0, неМеньше(тк.длинаММ, ПОРОГИ.тк));
    if (S) { const и = тк.длинаММ / S; р('Кольца', 'Кольцо ТК на ППТ', и, 'мм/м²', 1, неМеньше(и, ПОРОГИ.ткИндекс)); }
  }
  if (ок(мк)) р('Кольца', 'Кольцо МК', мк.длинаММ, 'мм', 0);

  if (!S && (ок(лп) || ок(пп) || ок(тк))) оговорки.push('Для индексов (на площадь тела) введите рост и вес.');
  return { строки, оговорки, ппт: S };
}

/** Оговорки вкладки камер, которые окно показывает всегда. */
export function оговоркиКамер() {
  return [
    'Пороги перенесены из рекомендаций по памяти, источник указан в строке; сверьте с действующими. «Выше порога» — рамка для глаза, не диагноз.',
    'Позиция настроена на ЛЖ: размеры и площади ПЖ в ней приблизительны, точнее — в ПЖ-фокусированной позиции.',
    'Объёмы ЛП и ПП — по одной плоскости (4-камерная); двухплоскостной расчёт точнее.',
    'Площадь тела — по Мостеллеру.',
  ];
}

export default { шкалаГлубины, шагИзТега, выбратьМасштаб, масштабПоМеткам, объёмДисками, итогСимпсона, оговоркиСимпсона,
  объёмБиплан, итогБиплана, ПОРОГИ_ДО, массаЛЖ, итогДлиннойОси, оговоркиДлиннойОси,
  отрезокММ, ппт, итогКамер, оговоркиКамер, ПОРОГИ };
